import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";

/* ------------------------------------------------------------------ */
/*  DifferenceGlyph                                                    */
/*                                                                     */
/*  A small hero-level SVG glyph that reads left-to-right as           */
/*  chaos → order. Eight scattered dots on the left, a thin champagne  */
/*  divider, and eight dots on the right that animate into a clean     */
/*  4×2 grid the moment the glyph scrolls into view.                   */
/*                                                                     */
/*  Designed to sit above the H1 on /the-difference. Height around     */
/*  56px on mobile, 72px on desktop. Pure SVG, no external assets.     */
/* ------------------------------------------------------------------ */

// Left side — chaotic scatter, hand-placed so it feels intentional
const chaosPoints = [
  { x: 12, y: 20 }, { x: 34, y: 8 }, { x: 48, y: 44 }, { x: 22, y: 52 },
  { x: 60, y: 24 }, { x: 78, y: 12 }, { x: 72, y: 54 }, { x: 88, y: 38 },
];

// Right side — clean 4×2 grid inside the same 100×64 bounding box
const orderPoints = [];
const cols = 4;
const rows = 2;
const startX = 12;
const stepX = 24;
const startY = 20;
const stepY = 24;
for (let r = 0; r < rows; r++) {
  for (let c = 0; c < cols; c++) {
    orderPoints.push({ x: startX + c * stepX, y: startY + r * stepY });
  }
}

// Deliberately messy starting positions for the RIGHT dots so they animate
// INTO the grid, not just fade in. This mirrors the chaos → order idea.
const orderStartOffsets = [
  { x: 6, y: -14 }, { x: -10, y: 8 }, { x: 14, y: 6 }, { x: -6, y: -12 },
  { x: 10, y: 14 }, { x: -12, y: -8 }, { x: 8, y: 10 }, { x: -14, y: 4 },
];

export default function DifferenceGlyph({ className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <div
      ref={ref}
      className={`inline-block ${className}`}
      aria-hidden="true"
      data-testid="difference-glyph"
    >
      <svg
        viewBox="0 0 224 64"
        className="h-14 md:h-[72px] w-auto"
        fill="none"
      >
        {/* -------------- LEFT · Chaos -------------- */}
        <g opacity={0.55}>
          {chaosPoints.map((p, i) => (
            <motion.circle
              key={`c-${i}`}
              cx={p.x}
              cy={p.y}
              r={2.4}
              fill="#8b6f47"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{
                duration: 0.6,
                delay: 0.02 * i,
                ease: [0.16, 1, 0.3, 1],
              }}
            />
          ))}
        </g>

        {/* -------------- CENTER · Divider -------------- */}
        <motion.line
          x1={112}
          x2={112}
          y1={8}
          y2={56}
          stroke="#C9A961"
          strokeOpacity={0.4}
          strokeWidth={0.8}
          strokeDasharray="2 3"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={inView ? { pathLength: 1, opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* -------------- RIGHT · Order (animates into grid) -------------- */}
        <g transform="translate(124, 0)">
          {orderPoints.map((p, i) => {
            const offset = orderStartOffsets[i];
            return (
              <motion.circle
                key={`o-${i}`}
                r={2.6}
                fill="#C9A961"
                initial={{
                  cx: p.x + offset.x,
                  cy: p.y + offset.y,
                  opacity: 0,
                  scale: 0.6,
                }}
                animate={
                  inView
                    ? {
                        cx: p.x,
                        cy: p.y,
                        opacity: 1,
                        scale: 1,
                      }
                    : {}
                }
                transition={{
                  duration: 1.0,
                  delay: 0.55 + 0.04 * i,
                  ease: [0.16, 1, 0.3, 1],
                }}
                style={{
                  filter: "drop-shadow(0 0 4px rgba(201,169,97,0.4))",
                }}
              />
            );
          })}

          {/* Faint connecting grid line, drawn after dots land */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 1.35 }}
          >
            <line x1={12} x2={84} y1={20} y2={20} stroke="#C9A961" strokeOpacity={0.18} strokeWidth={0.6} />
            <line x1={12} x2={84} y1={44} y2={44} stroke="#C9A961" strokeOpacity={0.18} strokeWidth={0.6} />
          </motion.g>
        </g>
      </svg>
    </div>
  );
}
